import React, { useRef, useEffect, useState, useCallback } from "react";
import { fabric } from "fabric";
import ObjectToolbar from "./ObjectToolbar";
import PreviewModal from "./PreviewModal";
import styles from "./CameraOverlay.module.css";
import { SvgAsset, SVG_LIBRARY_STORAGE_KEY } from "../types/svg";
import { CapturedPhoto, GALLERY_STORAGE_KEY } from "../types/gallery";
import { useTouchGestures } from "../hooks/useTouchGestures";

type CameraOverlayProps = {
  onOpenGallery?: () => void;
};

const CameraOverlay: React.FC<CameraOverlayProps> = ({ onOpenGallery }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const fabricCanvasRef = useRef<HTMLCanvasElement>(null);
  const fabricRef = useRef<fabric.Canvas | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const playPromiseRef = useRef<Promise<void> | null>(null);
  const cancelPlayRef = useRef<boolean>(false);
  
  const [videoReady, setVideoReady] = useState(false);
  const [isPreview, setIsPreview] = useState(false);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [restartTick, setRestartTick] = useState<number>(0);
  const [devices, setDevices] = useState<Array<{ label: string; deviceId: string }>>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>("");
  const [facingMode, setFacingMode] = useState<"user" | "environment">("environment");
  const [svgLibrary, setSvgLibrary] = useState<SvgAsset[]>([]);

  // Initialize touch gesture support for mobile pinch-to-zoom and rotation
  useTouchGestures({ canvas: fabricRef.current, enabled: true });

  const updateCanvasSize = useCallback(() => {
    const canvasEl = fabricCanvasRef.current;
    const fabricCanvas = fabricRef.current;
    if (!canvasEl || !fabricCanvas) return;

    const host = canvasEl.parentElement;
    const bounds = host?.getBoundingClientRect();
    const nextWidth = Math.max(1, Math.round(bounds?.width ?? window.innerWidth));
    const nextHeight = Math.max(1, Math.round(bounds?.height ?? window.innerHeight));

    if (!Number.isFinite(nextWidth) || !Number.isFinite(nextHeight)) return;

    const applySize = (element?: HTMLCanvasElement | HTMLDivElement | null) => {
      if (!element) return;
      element.style.width = `${nextWidth}px`;
      element.style.height = `${nextHeight}px`;
      if (element instanceof HTMLCanvasElement) {
        element.width = nextWidth;
        element.height = nextHeight;
      }
    };

    const internals = fabricCanvas as unknown as {
      lowerCanvasEl?: HTMLCanvasElement;
      upperCanvasEl?: HTMLCanvasElement;
      wrapperEl?: HTMLDivElement;
    };

    applySize(canvasEl);
    const lowerEl = internals.lowerCanvasEl ?? fabricCanvas.getElement();
    const upperEl = internals.upperCanvasEl ?? undefined;
    applySize(lowerEl);
    applySize(upperEl);
    applySize(fabricCanvas.getSelectionElement());
    const wrapperEl = internals.wrapperEl ?? undefined;
    applySize(wrapperEl);
    
    // Position Fabric.js wrapper to cover entire parent container
    if (wrapperEl) {
      wrapperEl.style.position = 'absolute';
      wrapperEl.style.top = '0';
      wrapperEl.style.left = '0';
      wrapperEl.style.right = '0';
      wrapperEl.style.bottom = '0';
      wrapperEl.style.zIndex = '2';
    }
    if (lowerEl) {
      lowerEl.style.position = 'absolute';
      lowerEl.style.top = '0';
      lowerEl.style.left = '0';
    }
    if (upperEl) {
      upperEl.style.position = 'absolute';
      upperEl.style.top = '0';
      upperEl.style.left = '0';
    }

    // Synchronize Fabric.js internal dimensions with DOM element sizes
    fabricCanvas.setDimensions({ width: nextWidth, height: nextHeight });
    fabricCanvas.setWidth(nextWidth);
    fabricCanvas.setHeight(nextHeight);
    if (lowerEl) {
      lowerEl.width = nextWidth;
      lowerEl.height = nextHeight;
    }
    if (upperEl) {
      upperEl.width = nextWidth;
      upperEl.height = nextHeight;
    }

    fabricCanvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
    fabricCanvas.calcOffset();
    fabricCanvas.requestRenderAll();
  }, []);

  const reloadSvgLibrary = useCallback(() => {
    try {
      const raw = localStorage.getItem(SVG_LIBRARY_STORAGE_KEY);
      if (!raw) {
        setSvgLibrary([]);
        return;
      }
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        setSvgLibrary(parsed as SvgAsset[]);
      } else {
        setSvgLibrary([]);
      }
    } catch (error) {
      console.warn("Failed to parse SVG library", error);
      setSvgLibrary([]);
    }
  }, []);

  const reloadGallery = useCallback(() => {
    try {
      const raw = localStorage.getItem(GALLERY_STORAGE_KEY);
      if (!raw) { return; }
      const parsed = JSON.parse(raw) as CapturedPhoto[];
      if (!Array.isArray(parsed)) {
        return; // Invalid format, skip
      }
    } catch (e) {
      console.warn("Failed to parse gallery", e);
    }
  }, []);

  useEffect(() => {
    reloadSvgLibrary();
    const handleUpdate = () => reloadSvgLibrary();
    window.addEventListener("svg-library-updated", handleUpdate);
    reloadGallery();
    const handleGallery = () => reloadGallery();
    window.addEventListener("gallery-updated", handleGallery);
    return () => {
      window.removeEventListener("svg-library-updated", handleUpdate);
      window.removeEventListener("gallery-updated", handleGallery);
    };
  }, [reloadSvgLibrary, reloadGallery]);
  

  
  useEffect(() => {
    let isMounted = true;
    let currentPlayPromise: Promise<void> | null = null;
    cancelPlayRef.current = false;
    
    // Capture video element reference early for reliable cleanup
    const videoEl = videoRef.current;

    const mediaDevices = navigator.mediaDevices;

    if (!mediaDevices?.getUserMedia) {
      setCameraError("Camera API is not available in this environment.");
      setVideoReady(false);
      setDevices([]);
      return () => {
        isMounted = false;
        cancelPlayRef.current = true;
      };
    }

    const startCamera = async () => {
      try {
        const stream = await mediaDevices.getUserMedia({
          video: selectedDeviceId 
            ? { deviceId: { exact: selectedDeviceId } }
            : { 
                facingMode: facingMode, 
                width: { ideal: 1920, max: 1920 }, 
                height: { ideal: 1080, max: 1080 }
              },
          audio: false
        });

        if (!isMounted || cancelPlayRef.current) {
          stream.getTracks().forEach(t => t.stop());
          return;
        }

        const video = videoRef.current;
        if (!video) {
          stream.getTracks().forEach(t => t.stop());
          return;
        }

        streamRef.current = stream;
        video.srcObject = stream;

        
        await new Promise<void>((resolve) => {
          if (video.readyState >= 2) {
            resolve();
          } else {
            const onLoaded = () => {
              video.removeEventListener("loadedmetadata", onLoaded);
              resolve();
            };
            video.addEventListener("loadedmetadata", onLoaded);
          }
        });

        if (!isMounted || cancelPlayRef.current) return;

        
        try {
          currentPlayPromise = video.play();
          playPromiseRef.current = currentPlayPromise;
          await currentPlayPromise;
          
          if (!isMounted || cancelPlayRef.current) return;
          
          setVideoReady(true);
          setCameraError(null);
        } catch (error: any) {
          
          if (error?.name === "AbortError" || !isMounted || cancelPlayRef.current) {
            return;
          }
          console.warn("Video play() error:", error?.name);
          if (isMounted) {
            setCameraError("Failed to play video");
            setVideoReady(false);
          }
        }
      } catch (err: any) {
        if (!isMounted) return;
        
        const name = err?.name || 'Error';
        let friendly = "Failed to start camera.";
        if (name === "NotAllowedError") friendly = "Camera access denied.";
        else if (name === "NotFoundError") friendly = "Camera not found.";
        else if (name === "NotReadableError") friendly = "Camera is busy by another application.";
        
        setCameraError(`${friendly} (${name})`);
      }
    };

    
    if (mediaDevices.enumerateDevices) {
      mediaDevices.enumerateDevices()
        .then(all => {
          if (!isMounted) return;
          const videos = all.filter(d => d.kind === 'videoinput');
          setDevices(videos.map(v => ({ 
            label: v.label || `Camera ${v.deviceId.slice(0, 6)}`, 
            deviceId: v.deviceId 
          })));
        })
        .catch(() => {});
    } else {
      setDevices([]);
    }

    startCamera();

    return () => {
      isMounted = false;
      cancelPlayRef.current = true;

      // Reference video element captured at effect initialization for cleanup safety
      const finishingPlayPromise = currentPlayPromise;
      const currentStream = streamRef.current;

      const cleanup = async () => {
        if (finishingPlayPromise) {
          try {
            await finishingPlayPromise;
          } catch (e) {
            
          }
        }

        if (currentStream) {
          currentStream.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
        }

        if (videoEl) {
          videoEl.srcObject = null;
        }

        playPromiseRef.current = null;
      };

      cleanup();
    };
  }, [restartTick, selectedDeviceId, facingMode]);

  

  const handleRetryCamera = () => setRestartTick((n) => n + 1);
  
  const handleSwitchCamera = useCallback(() => {
    setFacingMode(prev => prev === "environment" ? "user" : "environment");
    setRestartTick((n) => n + 1);
  }, []);

  
  useEffect(() => {
    if (!fabricCanvasRef.current || fabricRef.current) return;

    const canvasEl = fabricCanvasRef.current;

    try {
      const context = canvasEl.getContext("2d");
      if (!context) {
        console.warn("Canvas context unavailable; skipping Fabric setup.");
        return;
      }
    } catch (error) {
      console.warn("Canvas context not supported in this environment; skipping Fabric setup.", error);
      return;
    }

    // Pre-configure canvas dimensions before Fabric.js initialization to prevent default 300x150 size
    const host = canvasEl.parentElement;
    const bounds = host?.getBoundingClientRect();
    const initW = Math.max(1, Math.round(bounds?.width ?? window.innerWidth));
    const initH = Math.max(1, Math.round(bounds?.height ?? window.innerHeight));
    canvasEl.width = initW;
    canvasEl.height = initH;
    canvasEl.style.width = `${initW}px`;
    canvasEl.style.height = `${initH}px`;

    fabricRef.current = new fabric.Canvas(canvasEl, {
      selection: true,
      preserveObjectStacking: true,
      enableRetinaScaling: false,
      stateful: false,
      renderOnAddRemove: false,
      backgroundColor: 'transparent',
      skipOffscreen: true,
      stopContextMenu: true,
      fireRightClick: false,
      fireMiddleClick: false,
      uniformScaling: false,
      // Configure Fabric.js performance optimizations
      perPixelTargetFind: false,
      targetFindTolerance: 4,
    });

    // Define custom delete control with visual X icon positioned top-right
    const deleteIcon = "data:image/svg+xml,%3C%3Fxml version='1.0' encoding='utf-8'%3F%3E%3C!DOCTYPE svg PUBLIC '-//W3C//DTD SVG 1.1//EN' 'http://www.w3.org/Graphics/SVG/1.1/DTD/svg11.dtd'%3E%3Csvg version='1.1' id='Ebene_1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' x='0px' y='0px' width='595.275px' height='595.275px' viewBox='200 215 230 470' xml:space='preserve'%3E%3Ccircle style='fill:%23F44336;' cx='299.76' cy='439.067' r='218.516'/%3E%3Cg%3E%3Crect x='267.162' y='307.978' transform='matrix(0.7071 -0.7071 0.7071 0.7071 -222.6202 340.6915)' style='fill:white;' width='65.545' height='262.18'/%3E%3Crect x='266.988' y='308.153' transform='matrix(0.7071 0.7071 -0.7071 0.7071 398.3889 -83.3116)' style='fill:white;' width='65.544' height='262.179'/%3E%3C/g%3E%3C/svg%3E";
    
    const deleteImg = document.createElement('img');
    deleteImg.src = deleteIcon;

    function renderDeleteIcon(ctx: CanvasRenderingContext2D, left: number, top: number, styleOverride: any, fabricObject: fabric.Object) {
      const size = 24;
      ctx.save();
      ctx.translate(left, top);
      ctx.rotate(fabric.util.degreesToRadians(fabricObject.angle || 0));
      ctx.drawImage(deleteImg, -size/2, -size/2, size, size);
      ctx.restore();
    }

    function deleteObject(eventData: MouseEvent, transform: any) {
      const target = transform.target;
      const canvas = target.canvas;
      canvas.remove(target);
      canvas.requestRenderAll();
      return true;
    }

    (fabric.Object.prototype as any).controls.deleteControl = new fabric.Control({
      x: 0.5,
      y: -0.5,
      offsetY: -16,
      offsetX: 16,
      cursorStyle: 'pointer',
      mouseUpHandler: deleteObject,
      render: renderDeleteIcon,
      sizeX: 24,
      sizeY: 24
    });

  updateCanvasSize();

    const canvas = fabricRef.current;
    const ctx = canvas.getContext();
    if (ctx) {
      ctx.imageSmoothingEnabled = false;
    }

    // Implement throttled rendering during object transformations for performance
    let isTransforming = false;
    let rafId: number | null = null;

    function throttledRender() {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        fabricRef.current?.renderAll();
        rafId = null;
      });
    }

    // Attach optimized event handlers with RAF-based throttling
    canvas.on("object:added", () => fabricRef.current?.requestRenderAll());
    canvas.on("object:removed", () => fabricRef.current?.requestRenderAll());
    
    canvas.on("object:moving", () => {
      isTransforming = true;
      throttledRender();
    });
    canvas.on("object:scaling", throttledRender);
    canvas.on("object:rotating", throttledRender);
    
    canvas.on("object:modified", () => {
      isTransforming = false;
      fabricRef.current?.requestRenderAll();
    });
    
    canvas.on("mouse:up", () => {
      if (isTransforming) {
        isTransforming = false;
        fabricRef.current?.requestRenderAll();
      }
    });
    
    canvas.on("selection:created", () => fabricRef.current?.requestRenderAll());
    canvas.on("selection:cleared", () => fabricRef.current?.requestRenderAll());
    canvas.on("selection:updated", () => fabricRef.current?.requestRenderAll());
    
    
    
    return () => {
      if (fabricRef.current) {
        fabricRef.current.off();
        fabricRef.current.dispose();
        fabricRef.current = null;
      }
    };
  }, [updateCanvasSize]);

  
  useEffect(() => {
    updateCanvasSize();

    const handleResize = () => updateCanvasSize();
    window.addEventListener("resize", handleResize);

    const parent = fabricCanvasRef.current?.parentElement ?? null;
    const resizeObserver = typeof ResizeObserver !== "undefined" && parent
      ? new ResizeObserver(() => updateCanvasSize())
      : null;
    if (resizeObserver && parent) {
      resizeObserver.observe(parent);
    }

    const video = videoRef.current;
    const onLoadedMetadata = () => updateCanvasSize();
    video?.addEventListener("loadedmetadata", onLoadedMetadata);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      video?.removeEventListener("loadedmetadata", onLoadedMetadata);
    };
  }, [updateCanvasSize]);

  
  const handleAddObject = useCallback((type: string, imageSrc?: string) => {
    if (!fabricRef.current) return;
    
    // Detect mobile viewport for adaptive scaling
    const isMobile = window.innerWidth <= 768;
    const scaleFactor = isMobile ? 0.6 : 1.0;
    
    const shadowConfig = new fabric.Shadow({
      color: 'rgba(0,0,0,0.5)',
      blur: 20,
      offsetX: 8,
      offsetY: 8,
    });

    if (type === "rect") {
      const obj = new fabric.Rect({
        left: 100,
        top: 100,
        fill: "rgba(255, 215, 0, 0.85)",
        width: 80 * scaleFactor,
        height: 40 * scaleFactor,
        stroke: "rgba(255, 255, 255, 0.9)",
        strokeWidth: 3,
        rx: 5,
        ry: 5,
        shadow: shadowConfig,
        opacity: 0.92,
        cornerStyle: 'circle' as const,
      });
      fabricRef.current.add(obj);
      fabricRef.current.setActiveObject(obj);
      fabricRef.current.renderAll();
    } else if (type === "image" && imageSrc) {
      const isMobile = window.innerWidth <= 768;
      const scaleFactor = isMobile ? 0.25 : 0.35;
      
      fabric.Image.fromURL(imageSrc, (img) => {
        if (!img || !img.width || !img.height) {
          console.error('Failed to load image:', imageSrc);
          alert('Failed to load image. Please check the file format.');
          return;
        }
        
        console.log('✅ Image loaded:', { 
          src: imageSrc, 
          width: img.width, 
          height: img.height 
        });
        /**
         * Edge-based conservative background removal for oxygen-cylinder / air-purifier.
         * Goal: remove only true border background (near-white) while keeping full object including bottom.
         */
        try {
          const fileName = imageSrc.toLowerCase();
          const isCylinder = fileName.includes('oxygen-cylinder') || fileName.includes('cylinder');
          const isPurifier = fileName.includes('air-purifier') || fileName.includes('concentrator');
          // Apply background removal only to mini-fridge PNG asset
          const isFridge = fileName.includes('mini-fridge');
          const isBed1 = fileName.includes('hospital-bed-1');
          const isBed2 = fileName.includes('hospital-bed-2');
          const isWheelchair = fileName.includes('wheelchair');
          const isCurtainLeft = fileName.includes('curtain_left') || fileName.includes('curtain left');
          const isCurtainRight = fileName.includes('curtain right') || fileName.includes('curtain_right');
          const isHospitalCurtain1 = fileName.includes('hospital_curtain.png');
          const isHospitalCurtain2 = fileName.includes('hospital_curtain2.png');

          // Mini-fridge: edge-based background removal algorithm
          if (isFridge) {
            const element = img.getElement();
            const w = (element as HTMLImageElement).naturalWidth || img.width || 0;
            const h = (element as HTMLImageElement).naturalHeight || img.height || 0;
            if (w > 0 && h > 0) {
              const offCanvas = document.createElement('canvas');
              offCanvas.width = w;
              offCanvas.height = h;
              const offCtx = offCanvas.getContext('2d');
              if (offCtx) {
                offCtx.drawImage(element as HTMLImageElement, 0, 0, w, h);
                const imageData = offCtx.getImageData(0, 0, w, h);
                const data = imageData.data;
                const borderSamples: Array<[number, number, number]> = [];
                const sampleStep = 5;
                for (let x = 0; x < w; x += sampleStep) {
                  const top = (x + 0 * w) * 4;
                  const bottom = (x + (h - 1) * w) * 4;
                  borderSamples.push([data[top], data[top + 1], data[top + 2]]);
                  borderSamples.push([data[bottom], data[bottom + 1], data[bottom + 2]]);
                }
                for (let y = 0; y < h; y += sampleStep) {
                  const left = (0 + y * w) * 4;
                  const right = ((w - 1) + y * w) * 4;
                  borderSamples.push([data[left], data[left + 1], data[left + 2]]);
                  borderSamples.push([data[right], data[right + 1], data[right + 2]]);
                }
                const bins = new Map<string, { r: number; g: number; b: number; count: number }>();
                for (const [r, g, b] of borderSamples) {
                  const key = `${Math.round(r / 8) * 8}|${Math.round(g / 8) * 8}|${Math.round(b / 8) * 8}`;
                  const ex = bins.get(key);
                  if (ex) ex.count++; else bins.set(key, { r, g, b, count: 1 });
                }
                const bgColors = Array.from(bins.values())
                  .filter(c => (c.r + c.g + c.b) / 3 > 220)
                  .sort((a, b) => b.count - a.count)
                  .slice(0, 6);
                const dist = (r: number, g: number, b: number, c: { r: number; g: number; b: number }) => {
                  const dr = r - c.r, dg = g - c.g, db = b - c.b;
                  return Math.sqrt(dr * dr + dg * dg + db * db);
                };
                // Enhanced removal aggressiveness for mini-fridge asset
                // Runtime configuration via window global:
                //   window.IFM_FRIDGE_TRIM = { distance?: number, brightness?: number, hardMode?: boolean, expand?: number }
                // distance: color distance threshold for background detection
                // brightness: minimum brightness threshold for removal
                // hardMode: enables neighbor expansion and mid-tone removal
                // expand: neighbor expansion radius when hardMode enabled
                const cfg: any = (window as any).IFM_FRIDGE_TRIM || {};
                const fridgeBaseThreshold = Math.max(10, Math.min(80, Number(cfg.distance ?? 52))); // even higher trim default
                const brightnessGate = Math.max(140, Math.min(255, Number(cfg.brightness ?? 200))); // broader bright window
                const hardMode = Boolean(cfg.hardMode); // off by default
                const expandRadius = Math.max(1, Math.min(3, Number(cfg.expand ?? (hardMode ? 2 : 1))));

                // Pass 1: Remove pixels matching background colors by distance threshold
                const toRemove = new Uint8Array(w * h); // mark pixels to remove for second pass logic
                for (let i = 0; i < data.length; i += 4) {
                  const r = data[i], g = data[i + 1], b = data[i + 2];
                  const brightness = (r + g + b) / 3;
                  const ultraBright = brightness >= 235; // more pixels count as ultra-bright
                  if (brightness < brightnessGate && !ultraBright) continue;
                  for (const c of bgColors) {
                    const d = dist(r, g, b, c);
                    if (d <= fridgeBaseThreshold || (ultraBright && d <= fridgeBaseThreshold + 12)) {
                      toRemove[i / 4] = 1;
                      data[i + 3] = 0;
                      break;
                    }
                  }
                }

                // Pass 2 (hardMode): Expand transparency into neighboring bright pixels
                if (hardMode) {
                  const idxOf = (x: number, y: number) => (x + y * w) * 4;
                  for (let y = 1; y < h - 1; y++) {
                    for (let x = 1; x < w - 1; x++) {
                      const base = idxOf(x, y);
                      if (data[base + 3] === 0) continue; // already transparent
                      const r = data[base], g = data[base + 1], b = data[base + 2];
                      const brightness = (r + g + b) / 3;
                      if (brightness < (brightnessGate - 25)) continue; // only act on fairly bright
                      // Detect nearby removed pixels within expansion radius
                      let nearRemoved = 0;
                      for (let dy = -expandRadius; dy <= expandRadius && nearRemoved < 2; dy++) {
                        const ny = y + dy;
                        if (ny < 0 || ny >= h) continue;
                        for (let dx = -expandRadius; dx <= expandRadius && nearRemoved < 2; dx++) {
                          const nx = x + dx;
                          if (nx < 0 || nx >= w) continue;
                          const ni = (nx + ny * w);
                          if (toRemove[ni]) nearRemoved++;
                        }
                      }
                      if (nearRemoved >= 1) { // relax expansion trigger
                        // Apply relaxed threshold to neighbor pixels for expansion
                        for (const c of bgColors) {
                          const d = dist(r, g, b, c);
                          if (d <= fridgeBaseThreshold + 16) { // stronger expansion
                            data[base + 3] = 0;
                            toRemove[base / 4] = 1;
                            break;
                          }
                        }
                      }
                    }
                  }
                }
                const neighborOffsets = [ -4 * w, 4 * w, -4, 4 ];
                for (let y = 1; y < h - 1; y++) {
                  for (let x = 1; x < w - 1; x++) {
                    const idx = (x + y * w) * 4;
                    if (data[idx + 3] === 0) {
                      let darkerOpaque = 0;
                      for (const off of neighborOffsets) {
                        const n = idx + off;
                        if (n < 0 || n >= data.length) continue;
                        if (data[n + 3] > 0) {
                          const br = (data[n] + data[n + 1] + data[n + 2]) / 3;
                          if (br < 230) darkerOpaque++;
                        }
                      }
                      if (darkerOpaque >= 3) {
                        data[idx + 3] = 255;
                      }
                    }
                  }
                }
                offCtx.putImageData(imageData, 0, 0);
                const processedUrl = offCanvas.toDataURL('image/png');
                fabric.Image.fromURL(processedUrl, (processedImg) => {
                  if (!processedImg) return;
                  img = processedImg;
                  console.log('🧊 Mini Fridge background trimming applied:', { fileName, w, h, bgColors, fridgeBaseThreshold, brightnessGate });
                  continueWithImage(img);
                }, { crossOrigin: 'anonymous' });
                return; // stop normal flow
              }
            }
          }
          if (isCylinder) {
            const element = img.getElement();
            const w = (element as HTMLImageElement).naturalWidth || img.width || 0;
            const h = (element as HTMLImageElement).naturalHeight || img.height || 0;
            if (w > 0 && h > 0) {
              const offCanvas = document.createElement('canvas');
              offCanvas.width = w;
              offCanvas.height = h;
              const offCtx = offCanvas.getContext('2d');
              if (offCtx) {
                offCtx.drawImage(element as HTMLImageElement, 0, 0, w, h);
                const imageData = offCtx.getImageData(0, 0, w, h);
                const data = imageData.data;

                // Sample border pixels to identify dominant background colors
                const bgSamples: Array<[number, number, number]> = [];
                const sampleStep = 6;
                for (let x = 0; x < w; x += sampleStep) {
                  const topIdx = (x + 0 * w) * 4;
                  const botIdx = (x + (h - 1) * w) * 4;
                  bgSamples.push([data[topIdx], data[topIdx + 1], data[topIdx + 2]]);
                  bgSamples.push([data[botIdx], data[botIdx + 1], data[botIdx + 2]]);
                }
                for (let y = 0; y < h; y += sampleStep) {
                  const leftIdx = (0 + y * w) * 4;
                  const rightIdx = ((w - 1) + y * w) * 4;
                  bgSamples.push([data[leftIdx], data[leftIdx + 1], data[leftIdx + 2]]);
                  bgSamples.push([data[rightIdx], data[rightIdx + 1], data[rightIdx + 2]]);
                }

                // Bin sampled colors into clusters for background identification
                const bins = new Map<string, { r: number; g: number; b: number; count: number }>();
                for (const [r, g, b] of bgSamples) {
                  const key = `${Math.round(r / 8) * 8}|${Math.round(g / 8) * 8}|${Math.round(b / 8) * 8}`;
                  const ex = bins.get(key);
                  if (ex) ex.count++; else bins.set(key, { r, g, b, count: 1 });
                }
                const bgColors = Array.from(bins.values())
                  .sort((a, b) => b.count - a.count)
                  .slice(0, 5); // top 5 frequent border colors

                // Calculate Euclidean distance in RGB color space
                const dist = (r: number, g: number, b: number, c: { r: number; g: number; b: number }) => {
                  const dr = r - c.r, dg = g - c.g, db = b - c.b;
                  return Math.sqrt(dr * dr + dg * dg + db * db);
                };

                // Conservative threshold for bright backgrounds typical of medical equipment
                // Reduce threshold dynamically for very bright backgrounds
                const avgBrightness = bgColors.reduce((sum, c) => sum + (c.r + c.g + c.b) / 3, 0) / Math.max(1, bgColors.length);
                const baseThreshold = avgBrightness > 240 ? 18 : 26; // tighter when very bright
                const threshold = baseThreshold - 4; // more conservative for cylinder

                // Pass 1: Identify and mark background pixels for removal
                for (let i = 0; i < data.length; i += 4) {
                  const r = data[i], g = data[i + 1], b = data[i + 2];
                  // Skip dark pixels to preserve equipment detail
                  if (r < 225 && g < 225 && b < 225) continue;
                  for (const c of bgColors) {
                    if (dist(r, g, b, c) <= threshold) {
                      data[i + 3] = 0; // transparent
                      break;
                    }
                  }
                }

                // Pass 2: Restore isolated transparent pixels surrounded by opaque neighbors
                // Restore if 3+ neighbors are opaque and darker than threshold
                const neighborOffsets = [
                  -4 * w, // up
                  4 * w,  // down
                  -4,     // left
                  4       // right
                ];
                for (let y = 1; y < h - 1; y++) {
                  for (let x = 1; x < w - 1; x++) {
                    const idx = (x + y * w) * 4;
                    if (data[idx + 3] === 0) {
                      let opaqueDarkNeighbors = 0;
                      for (const off of neighborOffsets) {
                        const ni = idx + off;
                        if (ni < 0 || ni >= data.length) continue;
                        if (data[ni + 3] > 0) {
                          const nr = data[ni], ng = data[ni + 1], nb = data[ni + 2];
                          const brightness = (nr + ng + nb) / 3;
                          if (brightness < 230) opaqueDarkNeighbors++;
                        }
                      }
                      if (opaqueDarkNeighbors >= 3) {
                        // Restore pixel opacity
                        data[idx + 3] = 255;
                      }
                    }
                  }
                }

                offCtx.putImageData(imageData, 0, 0);
                const processedUrl = offCanvas.toDataURL('image/png');
                // Swap original image with processed version before canvas addition
                fabric.Image.fromURL(processedUrl, (processedImg) => {
                  if (!processedImg) return;
                  img = processedImg; // reassign reference to continue normal flow
                  console.log('🧪 Edge-based background removal applied:', {
                    fileName,
                    originalWidth: w,
                    originalHeight: h,
                    bgColors,
                    threshold,
                  });
                  continueWithImage(img);
                }, { crossOrigin: 'anonymous' });
                return; // Prevent continuing with unprocessed image
              }
            }
          }

          // Air purifier: Apply minimal color-based background removal
          if (isPurifier) {
            const Filters: any = (fabric.Image as any).filters;
            const hasRemove = Filters && typeof Filters.RemoveColor === 'function';
            if (hasRemove) {
              const removeWhite = new Filters.RemoveColor({ color: '#ffffff', distance: 0.08 });
              const removeLightGray = new Filters.RemoveColor({ color: '#f5f5f5', distance: 0.04 });
              const removeBeige = new Filters.RemoveColor({ color: '#fafafa', distance: 0.03 });
              img.filters = [removeWhite, removeLightGray, removeBeige];
              (img as any).applyFilters?.();
              console.log('🎨 Restored minimal background removal (air-purifier).');
            } else {
              console.log('⚠️ RemoveColor filter not available for air-purifier');
            }
          }

          // Hospital beds: Apply conservative near-white background removal
          if (isBed1 || isBed2) {
            try {
              const Filters: any = (fabric.Image as any).filters;
              if (Filters && typeof Filters.RemoveColor === 'function') {
                // Runtime configuration: window.IFM_BED_TRIM for threshold control
                const cfg: any = (window as any).IFM_BED_TRIM || {};
                if (cfg.enabled === false) {
                  console.log('🛏️ Bed trim disabled via IFM_BED_TRIM');
                } else {
                  const dists: number[] = Array.isArray(cfg.distances) && cfg.distances.length
                    ? cfg.distances
                    : [0.08, 0.05, 0.04]; // conservative
                  const removeWhite = new Filters.RemoveColor({ color: '#ffffff', distance: dists[0] });
                  const removeNearWhite1 = new Filters.RemoveColor({ color: '#f7f7f7', distance: dists[1] });
                  const removeNearWhite2 = new Filters.RemoveColor({ color: '#f0f0f0', distance: dists[2] });
                  img.filters = [removeWhite, removeNearWhite1, removeNearWhite2];
                  (img as any).applyFilters?.();
                  console.log('🛏️ Applied conservative near-white trim (hospital bed).', { dists });
                }
              }
            } catch {}
          }

          // Wheelchair: Aggressive white/gray background removal for noisy PNG
          if (isWheelchair) {
            const element = img.getElement();
            const w = (element as HTMLImageElement).naturalWidth || img.width || 0;
            const h = (element as HTMLImageElement).naturalHeight || img.height || 0;
            if (w > 0 && h > 0) {
              const offCanvas = document.createElement('canvas');
              offCanvas.width = w;
              offCanvas.height = h;
              const offCtx = offCanvas.getContext('2d');
              if (offCtx) {
                offCtx.drawImage(element as HTMLImageElement, 0, 0, w, h);
                const imageData = offCtx.getImageData(0, 0, w, h);
                const data = imageData.data;
                
                // Sample border pixels to identify background color clusters
                const borderSamples: Array<[number, number, number]> = [];
                const sampleStep = 5;
                for (let x = 0; x < w; x += sampleStep) {
                  const top = (x + 0 * w) * 4;
                  const bottom = (x + (h - 1) * w) * 4;
                  borderSamples.push([data[top], data[top + 1], data[top + 2]]);
                  borderSamples.push([data[bottom], data[bottom + 1], data[bottom + 2]]);
                }
                for (let y = 0; y < h; y += sampleStep) {
                  const left = (0 + y * w) * 4;
                  const right = ((w - 1) + y * w) * 4;
                  borderSamples.push([data[left], data[left + 1], data[left + 2]]);
                  borderSamples.push([data[right], data[right + 1], data[right + 2]]);
                }
                
                // Extract dominant bright colors from border samples
                const bins = new Map<string, { r: number; g: number; b: number; count: number }>();
                for (const [r, g, b] of borderSamples) {
                  const key = `${Math.round(r / 8) * 8}|${Math.round(g / 8) * 8}|${Math.round(b / 8) * 8}`;
                  const ex = bins.get(key);
                  if (ex) ex.count++; else bins.set(key, { r, g, b, count: 1 });
                }
                const bgColors = Array.from(bins.values())
                  .filter(c => (c.r + c.g + c.b) / 3 > 200)
                  .sort((a, b) => b.count - a.count)
                  .slice(0, 6);
                
                const dist = (r: number, g: number, b: number, c: { r: number; g: number; b: number }) => {
                  const dr = r - c.r, dg = g - c.g, db = b - c.b;
                  return Math.sqrt(dr * dr + dg * dg + db * db);
                };
                
                // Apply aggressive threshold for noisy backgrounds
                const threshold = 55; // increased for noisy backgrounds
                const brightnessGate = 190; // remove bright pixels
                
                for (let i = 0; i < data.length; i += 4) {
                  const r = data[i], g = data[i + 1], b = data[i + 2];
                  const brightness = (r + g + b) / 3;
                  const ultraBright = brightness >= 235;
                  
                  if (brightness < brightnessGate && !ultraBright) continue;
                  
                  for (const c of bgColors) {
                    const d = dist(r, g, b, c);
                    if (d <= threshold || (ultraBright && d <= threshold + 15)) {
                      data[i + 3] = 0;
                      break;
                    }
                  }
                }
                
                // Restore isolated object pixels misidentified as background
                const neighborOffsets = [ -4 * w, 4 * w, -4, 4 ];
                for (let y = 1; y < h - 1; y++) {
                  for (let x = 1; x < w - 1; x++) {
                    const idx = (x + y * w) * 4;
                    if (data[idx + 3] === 0) {
                      let darkerOpaque = 0;
                      for (const off of neighborOffsets) {
                        const n = idx + off;
                        if (n < 0 || n >= data.length) continue;
                        if (data[n + 3] > 0) {
                          const br = (data[n] + data[n + 1] + data[n + 2]) / 3;
                          if (br < 220) darkerOpaque++;
                        }
                      }
                      if (darkerOpaque >= 3) {
                        data[idx + 3] = 255;
                      }
                    }
                  }
                }
                
                offCtx.putImageData(imageData, 0, 0);
                const processedUrl = offCanvas.toDataURL('image/png');
                fabric.Image.fromURL(processedUrl, (processedImg) => {
                  if (!processedImg) return;
                  img = processedImg;
                  console.log('♿ Wheelchair noisy background removed:', { w, h, bgColors, threshold });
                  continueWithImage(img);
                }, { crossOrigin: 'anonymous' });
                return;
              }
            }
          }

          // Curtains: Apply white background removal with soft threshold
          if (isCurtainLeft || isCurtainRight || isHospitalCurtain1 || isHospitalCurtain2) {
            const element = img.getElement();
            const w = (element as HTMLImageElement).naturalWidth || img.width || 0;
            const h = (element as HTMLImageElement).naturalHeight || img.height || 0;
            if (w > 0 && h > 0) {
              const offCanvas = document.createElement('canvas');
              offCanvas.width = w;
              offCanvas.height = h;
              const offCtx = offCanvas.getContext('2d');
              if (offCtx) {
                offCtx.drawImage(element as HTMLImageElement, 0, 0, w, h);
                const imageData = offCtx.getImageData(0, 0, w, h);
                const data = imageData.data;
                
                const isHospitalCurtain = isHospitalCurtain1 || isHospitalCurtain2;
                
                // Sample border region to identify background
                const borderSamples: Array<[number, number, number]> = [];
                const sampleStep = 5;
                
                for (let x = 0; x < w; x += sampleStep) {
                  const top = (x + 0 * w) * 4;
                  const bottom = (x + (h - 1) * w) * 4;
                  borderSamples.push([data[top], data[top + 1], data[top + 2]]);
                  borderSamples.push([data[bottom], data[bottom + 1], data[bottom + 2]]);
                }
                for (let y = 0; y < h; y += sampleStep) {
                  const left = (0 + y * w) * 4;
                  const right = ((w - 1) + y * w) * 4;
                  borderSamples.push([data[left], data[left + 1], data[left + 2]]);
                  borderSamples.push([data[right], data[right + 1], data[right + 2]]);
                }
                
                const bins = new Map<string, { r: number; g: number; b: number; count: number }>();
                for (const [r, g, b] of borderSamples) {
                  const key = `${Math.round(r / 8) * 8}|${Math.round(g / 8) * 8}|${Math.round(b / 8) * 8}`;
                  const ex = bins.get(key);
                  if (ex) ex.count++; else bins.set(key, { r, g, b, count: 1 });
                }
                
                const bgColors = Array.from(bins.values())
                  .filter(c => (c.r + c.g + c.b) / 3 > 200)
                  .sort((a, b) => b.count - a.count)
                  .slice(0, 6);
                
                const dist = (r: number, g: number, b: number, c: { r: number; g: number; b: number }) => {
                  const dr = r - c.r, dg = g - c.g, db = b - c.b;
                  return Math.sqrt(dr * dr + dg * dg + db * db);
                };
                
                // Use conservative threshold to preserve curtain detail
                const threshold = isHospitalCurtain ? 8 : 55;
                const brightnessGate = isHospitalCurtain ? 250 : 190;
                
                // Pass 1: Remove bright background pixels
                for (let i = 0; i < data.length; i += 4) {
                  const r = data[i], g = data[i + 1], b = data[i + 2];
                  const brightness = (r + g + b) / 3;
                  const ultraBright = brightness >= 235;
                  
                  if (brightness < brightnessGate && !ultraBright) continue;
                  
                  for (const c of bgColors) {
                    const d = dist(r, g, b, c);
                    if (d <= threshold || (ultraBright && d <= threshold + 15)) {
                      data[i + 3] = 0;
                      break;
                    }
                  }
                }
                
                // Pass 2: Restore isolated object pixels and clean artifacts
                const neighborOffsets = [ -4 * w, 4 * w, -4, 4 ];
                for (let y = 1; y < h - 1; y++) {
                  for (let x = 1; x < w - 1; x++) {
                    const idx = (x + y * w) * 4;
                    if (data[idx + 3] === 0) {
                      let darkerOpaque = 0;
                      for (const off of neighborOffsets) {
                        const n = idx + off;
                        if (n < 0 || n >= data.length) continue;
                        if (data[n + 3] > 0) {
                          const br = (data[n] + data[n + 1] + data[n + 2]) / 3;
                          if (br < 220) darkerOpaque++;
                        }
                      }
                      if (darkerOpaque >= 3) {
                        data[idx + 3] = 255;
                      }
                    }
                  }
                }
                
                offCtx.putImageData(imageData, 0, 0);
                const processedUrl = offCanvas.toDataURL('image/png');
                fabric.Image.fromURL(processedUrl, (processedImg) => {
                  if (!processedImg) return;
                  img = processedImg;
                  const curtainType = isCurtainLeft ? 'left' : isCurtainRight ? 'right' : isHospitalCurtain1 ? '1' : '2';
                  console.log(`🪟 Curtain ${curtainType} background removed:`, { w, h, bgColors, threshold });
                  continueWithImage(img);
                }, { crossOrigin: 'anonymous' });
                return;
              }
            }
          }
        } catch (e) {
          console.warn('⚠️ Edge background removal failed, proceeding without it:', e);
        }

        // Default path for assets not requiring special processing
        continueWithImage(img);

        function continueWithImage(finalImg: fabric.Image) {
          // Calculate scale factor for responsive canvas fit
          const maxDimension = Math.max(finalImg.width || 0, finalImg.height || 0);
          const targetSize = isMobile ? 200 : 300;
          const autoScale = maxDimension > 0 ? targetSize / maxDimension : scaleFactor;
          const finalScale = Math.min(autoScale, scaleFactor);

          finalImg.set({
            left: 140,
            top: 140,
            scaleX: finalScale,
            scaleY: finalScale,
            shadow: shadowConfig,
            opacity: 0.95,
            cornerStyle: 'circle' as const,
            objectCaching: true,
            statefullCache: true,
            noScaleCache: false,
            cacheProperties: ['fill', 'stroke', 'strokeWidth', 'strokeDashArray', 'width', 'height'],
            lockRotation: false,
            lockScalingX: false,
            lockScalingY: false,
            lockMovementX: false,
            lockMovementY: false,
            hasControls: true,
            hasBorders: true,
            selectable: true,
            evented: true,
          });
          fabricRef.current!.add(finalImg);
          fabricRef.current!.setActiveObject(finalImg);
          fabricRef.current!.requestRenderAll();
          console.log('✅ PNG image added to canvas with full interaction');
          if (imageSrc && imageSrc.startsWith('blob:')) {
            URL.revokeObjectURL(imageSrc);
          }
    }
    }); // end fabric.Image.fromURL callback
  } else if (type === "svg" && imageSrc) {
      const source = imageSrc;
      const urlToFetch = /^data:|^blob:/i.test(source) ? source : encodeURI(source);
      fetch(urlToFetch)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP ${response.status} while fetching ${urlToFetch}`);
          }
          return response.text();
        })
        .then((svgText) => {
          fabric.loadSVGFromString(svgText, (objects, options) => {
            // Remove large white/black background rectangles from SVG before grouping
            try {
              const toColorString = (fill: any) => (typeof fill === 'string' ? String(fill).toLowerCase() : '');
              const parseRGB = (fill: any): {r:number;g:number;b:number}|null => {
                try {
                  if (!fill || typeof fill !== 'string') return null;
                  const f = toColorString(fill).trim();
                  // Handle named color values (white, black)
                  if (f === 'white') return { r:255, g:255, b:255 };
                  if (f === 'black') return { r:0, g:0, b:0 };
                  // Parse hex color codes (#rgb, #rrggbb)
                  if (/^#([0-9a-f]{3}){1,2}$/i.test(f)) {
                    let r:number, g:number, b:number;
                    if (f.length === 4) {
                      r = parseInt(f[1]+f[1],16);
                      g = parseInt(f[2]+f[2],16);
                      b = parseInt(f[3]+f[3],16);
                    } else {
                      r = parseInt(f.slice(1,3),16);
                      g = parseInt(f.slice(3,5),16);
                      b = parseInt(f.slice(5,7),16);
                    }
                    return { r,g,b };
                  }
                  // Parse RGB/RGBA functional notation
                  const m = f.match(/^rgba?\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
                  if (m) return { r: +m[1], g: +m[2], b: +m[3] };
                } catch {}
                return null;
              };
              const isNearWhiteOrBlack = (fill: any) => {
                const fstr = toColorString(fill).replace(/\s+/g, '');
                if (
                  fstr === '#fff' || fstr === '#ffffff' || fstr === 'white' || fstr === 'rgb(255,255,255)' ||
                  fstr === '#000' || fstr === '#000000' || fstr === 'black' || fstr === 'rgb(0,0,0)'
                ) return true;
                const rgb = parseRGB(fill);
                if (!rgb) return false;
                const nearWhite = rgb.r >= 245 && rgb.g >= 245 && rgb.b >= 245;
                const nearBlack = rgb.r <= 12 && rgb.g <= 12 && rgb.b <= 12;
                return nearWhite || nearBlack;
              };

              // Calculate global bounding box for all SVG elements
              let minX = Number.POSITIVE_INFINITY;
              let minY = Number.POSITIVE_INFINITY;
              let maxX = Number.NEGATIVE_INFINITY;
              let maxY = Number.NEGATIVE_INFINITY;
              const getBR = (o: any) => (typeof o.getBoundingRect === 'function'
                ? o.getBoundingRect(true, true)
                : { left: o.left || 0, top: o.top || 0, width: (o.width || 0) * (o.scaleX || 1), height: (o.height || 0) * (o.scaleY || 1) });
              (objects as any[]).forEach(o => {
                const br = getBR(o);
                minX = Math.min(minX, br.left);
                minY = Math.min(minY, br.top);
                maxX = Math.max(maxX, br.left + br.width);
                maxY = Math.max(maxY, br.top + br.height);
              });
              const globalW = Math.max(1, maxX - minX);
              const globalH = Math.max(1, maxY - minY);
              const globalA = globalW * globalH;

              const filtered = (objects as any[]).filter((o) => {
                if (!o) return false;
                // Filter for background-type shapes (rect, path, polygon)
                const typeOk = o.type === 'rect' || o.type === 'path' || o.type === 'polygon' || o.type === 'ellipse' || o.type === 'circle';
                if (typeOk && isNearWhiteOrBlack((o as any).fill)) {
                  const br = getBR(o);
                  const coverW = br.width / globalW;
                  const coverH = br.height / globalH;
                  const areaFrac = (br.width * br.height) / globalA;
                  const coversAlmostAll = (coverW >= 0.9 && coverH >= 0.9) || areaFrac >= 0.82;
                  if (coversAlmostAll) {
                    return false; // drop background
                  }
                }
                return true; // keep
              });
              objects = filtered as any;

              // Fallback to raster processing if pure black fills detected
              const hasPureBlackFill = (fill: any) => {
                const rgb = parseRGB(fill);
                return !!rgb && rgb.r <= 5 && rgb.g <= 5 && rgb.b <= 5;
              };
              const needsRasterBlackRemoval = (objects as any[]).some((o) => {
                const f = (o as any).fill;
                if (!f || f === 'none') return false;
                return hasPureBlackFill(f);
              });

              if (needsRasterBlackRemoval) {
                const svgDataUrl = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgText);
                fabric.Image.fromURL(svgDataUrl, (img) => {
                  try {
                    const Filters: any = (fabric.Image as any).filters;
                    if (Filters && typeof Filters.RemoveColor === 'function') {
                      const black = new Filters.RemoveColor({ color: '#000000', distance: 0.12 });
                      img.filters = [black];
                      (img as any).applyFilters?.();
                    }
                  } catch {}

                  img.set({
                    left: 140,
                    top: 140,
                    shadow: shadowConfig,
                    opacity: 0.95,
                    cornerStyle: 'circle' as const,
                    objectCaching: true,
                    statefullCache: true,
                    noScaleCache: false,
                  });

                  const iw = img.width ?? 0;
                  const ih = img.height ?? 0;
                  const maxDim = Math.max(iw, ih);
                  if (maxDim > 0) {
                    const isMobile = window.innerWidth <= 768;
                    const target = isMobile ? 150 : 200;
                    const factor = Math.min(1, target / maxDim);
                    img.scale(factor);
                  }

                  fabricRef.current!.add(img);
                  fabricRef.current!.setActiveObject(img);
                  fabricRef.current!.requestRenderAll();
                }, { crossOrigin: 'anonymous' });
                return; // Skip vector add path, we've added rasterized image instead
              }
            } catch {}

            const grouped = fabric.util.groupSVGElements(
              objects as fabric.Object[],
              options || {}
            );
            grouped.set({
              left: 140,
              top: 140,
              shadow: shadowConfig,
              opacity: 0.95,
              cornerStyle: 'circle' as const,
              objectCaching: true,
              statefullCache: true,
              noScaleCache: false,
            });

            const width = grouped.width ?? 0;
            const height = grouped.height ?? 0;
            const maxDim = Math.max(width, height);
            if (maxDim > 0) {
              const isMobile = window.innerWidth <= 768;
              const target = isMobile ? 150 : 200;
              const factor = Math.min(1, target / maxDim);
              grouped.scale(factor);
            }

            fabricRef.current!.add(grouped);
            fabricRef.current!.setActiveObject(grouped);
            fabricRef.current!.requestRenderAll();
          });
        })
        .catch((error) => {
          console.error("SVG load error", error);
          alert(`Could not add SVG (${error}). Please check the path or the file.`);
        })
        .finally(() => {
          if (source.startsWith("blob:")) {
            URL.revokeObjectURL(source);
          }
        });
    } else if (type === "circle") {
      const isMobile = window.innerWidth <= 768;
      const radius = isMobile ? 20 : 30;
      
      const obj = new fabric.Circle({
        left: 100,
        top: 100,
        fill: "rgba(135, 206, 250, 0.85)",
        radius: radius,
        stroke: "rgba(255, 255, 255, 0.9)",
        strokeWidth: 3,
        shadow: shadowConfig,
        opacity: 0.92,
        cornerStyle: 'circle' as const,
      });
      fabricRef.current.add(obj);
      fabricRef.current.setActiveObject(obj);
      fabricRef.current.renderAll();
    }
  }, []);

  
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Delete" && fabricRef.current) {
        const active = fabricRef.current.getActiveObject();
        if (active) {
          fabricRef.current.remove(active);
          fabricRef.current.renderAll();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  
  const handleCapture = useCallback(() => {
    if (!videoRef.current || !fabricRef.current || !fabricCanvasRef.current) {
      console.error("Video or Fabric canvas not ready");
      alert("Components not ready. Please try again.");
      return;
    }
    
    const video = videoRef.current;
    const fabricCanvas = fabricRef.current;
    
    
    if (!videoReady || video.readyState < 2 || video.paused) {
      console.error("Video not ready", { 
        videoReady, 
        readyState: video.readyState, 
        paused: video.paused 
      });
      alert("Video not ready yet, please try again");
      return;
    }

    
    requestAnimationFrame(() => {
      
      const videoWidth = video.videoWidth;
      const videoHeight = video.videoHeight;

      console.log("=== CAPTURE START ===");
      console.log("Video dimensions:", { 
        videoWidth, 
        videoHeight,
        readyState: video.readyState,
        paused: video.paused,
        currentTime: video.currentTime
      });

      if (videoWidth === 0 || videoHeight === 0) {
        console.error("Video dimensions are zero!");
        alert("Cannot capture image - invalid video dimensions");
        return;
      }

      
      const tempCanvas = document.createElement("canvas");
      tempCanvas.width = videoWidth;
      tempCanvas.height = videoHeight;
      
      console.log("Canvas created:", { width: tempCanvas.width, height: tempCanvas.height });
      
      const ctx = tempCanvas.getContext("2d", { 
        alpha: true,
        willReadFrequently: false 
      });
      
      if (!ctx) {
        console.error("Failed to get canvas context");
        alert("Error creating canvas");
        return;
      }

      try {
        
        ctx.fillStyle = "#000000";
        ctx.fillRect(0, 0, videoWidth, videoHeight);
        console.log("Black background drawn");

        
        ctx.drawImage(video, 0, 0, videoWidth, videoHeight);
        console.log("Video drawn to canvas");
        
        
        const imageData = ctx.getImageData(0, 0, 1, 1);
        console.log("First pixel after video draw:", imageData.data);

        
        const fabricElement = fabricCanvas.getElement();
        const fabricWidth = fabricElement.width;
        const fabricHeight = fabricElement.height;
        
        console.log("Fabric canvas:", { fabricWidth, fabricHeight });
        
        if (fabricWidth > 0 && fabricHeight > 0) {
          
          fabricCanvas.renderAll();
          
          
          const scaleX = videoWidth / fabricWidth;
          const scaleY = videoHeight / fabricHeight;
          
          console.log("Scale factors:", { scaleX, scaleY });
          
          ctx.save();
          ctx.scale(scaleX, scaleY);
          ctx.drawImage(fabricElement, 0, 0);
          ctx.restore();
          
          console.log("Fabric overlay drawn");
        }

        
        const finalImage = tempCanvas.toDataURL("image/jpeg", 0.92);
        console.log("Final image created, length:", finalImage.length);
        console.log("Data URL prefix:", finalImage.substring(0, 50));
        
        if (finalImage.length < 1000) {
          console.error("Image too small, might be empty!");
          alert("Image seems empty. Length: " + finalImage.length);
          return;
        }
        
        console.log("=== CAPTURE SUCCESS ===");
        setCapturedImage(finalImage);
        setIsPreview(true);
      } catch (error) {
        console.error("Error during capture:", error);
        alert("Error during capture: " + error);
      }
    });
  }, [videoReady]);

  const addToGallery = useCallback((image: string) => {
    try {
      const raw = localStorage.getItem(GALLERY_STORAGE_KEY);
      const list: CapturedPhoto[] = raw ? JSON.parse(raw) : [];
      const item: CapturedPhoto = {
        id: globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`,
        dataUrl: image,
        createdAt: Date.now(),
      };
      const next = [item, ...list].slice(0, 60); // cap at 60 items
      localStorage.setItem(GALLERY_STORAGE_KEY, JSON.stringify(next));
      window.dispatchEvent(new Event("gallery-updated"));
    } catch (e) {
      console.error("Failed to add to gallery", e);
      alert("Failed to save to gallery.");
    }
  }, []);

  const handleSaveLayout = useCallback(async () => {
    if (!fabricRef.current || !videoRef.current || !fabricCanvasRef.current) {
      alert("Canvas is not ready.");
      return;
    }
    
    const video = videoRef.current;
    const fabricCanvas = fabricRef.current;
    
    // Validate video stream readiness before capture
    if (!videoReady || video.readyState < 2 || video.paused) {
      alert("Video not ready yet, please try again");
      return;
    }
    
    try {
      // Composite video frame with canvas overlay
      requestAnimationFrame(async () => {
        const videoWidth = video.videoWidth;
        const videoHeight = video.videoHeight;

        console.log("💾 Saving layout as image:", { videoWidth, videoHeight });

        if (videoWidth === 0 || videoHeight === 0) {
          alert("Cannot save layout - invalid video dimensions");
          return;
        }

        // Initialize temporary canvas for image composition
        const tempCanvas = document.createElement("canvas");
        tempCanvas.width = videoWidth;
        tempCanvas.height = videoHeight;
        
        const ctx = tempCanvas.getContext("2d", { 
          alpha: false,
          willReadFrequently: false 
        });
        
        if (!ctx) {
          alert("Error creating canvas");
          return;
        }

        try {
          // Render video frame to canvas
          ctx.fillStyle = "#000000";
          ctx.fillRect(0, 0, videoWidth, videoHeight);
          ctx.drawImage(video, 0, 0, videoWidth, videoHeight);
          
          // Render Fabric.js canvas overlay with scale correction
          const fabricElement = fabricCanvas.getElement();
          const fabricWidth = fabricElement.width;
          const fabricHeight = fabricElement.height;
          
          if (fabricWidth > 0 && fabricHeight > 0) {
            fabricCanvas.renderAll();
            
            const scaleX = videoWidth / fabricWidth;
            const scaleY = videoHeight / fabricHeight;
            
            ctx.save();
            ctx.scale(scaleX, scaleY);
            ctx.drawImage(fabricElement, 0, 0);
            ctx.restore();
          }

          // Export as PNG for lossless quality
          const layoutImage = tempCanvas.toDataURL("image/png", 1.0);
          
          if (layoutImage.length < 1000) {
            alert("Image seems empty. Length: " + layoutImage.length);
            return;
          }
          
          // Convert canvas to blob for file operations
          const base64Data = layoutImage.split(',')[1];
          const byteCharacters = atob(base64Data);
          const byteArrays = [];
          
          for (let offset = 0; offset < byteCharacters.length; offset += 1024) {
            const slice = byteCharacters.slice(offset, offset + 1024);
            const byteNumbers = new Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
              byteNumbers[i] = slice.charCodeAt(i);
            }
            byteArrays.push(new Uint8Array(byteNumbers));
          }
          
          const blob = new Blob(byteArrays, { type: 'image/png' });
          const filename = `layout-${Date.now()}.png`;
          
          console.log('💾 Saving layout image:', filename);
          
          // Attempt modern File System Access API for save dialog
          if ('showSaveFilePicker' in window) {
            try {
              const handle = await (window as any).showSaveFilePicker({
                suggestedName: filename,
                types: [{
                  description: 'Layout Images',
                  accept: { 'image/png': ['.png'], 'image/jpeg': ['.jpg', '.jpeg'] }
                }]
              });
              
              const writable = await handle.createWritable();
              await writable.write(blob);
              await writable.close();
              
              console.log('✅ Layout saved to file system:', handle.name);
              alert(`✅ Layout saved as image: ${handle.name}!`);
              return;
            } catch (e) {
              if ((e as Error).name === 'AbortError') {
                console.log('⚠️ User cancelled save');
                return;
              }
              console.log('📱 Falling back to download method');
            }
          }
          
          // Fallback to download attribute for compatibility
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          a.style.display = 'none';
          document.body.appendChild(a);
          a.click();
          
          // Clean up temporary DOM elements and object URLs
          setTimeout(() => {
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          }, 100);
          
          console.log('✅ Layout downloaded:', filename);
          alert(`✅ Layout saved as image to Downloads folder: ${filename}`);
        } catch (error) {
          console.error("Error during layout save:", error);
          alert("Error during save: " + error);
        }
      });
    } catch (e) {
      console.error("❌ Save layout failed:", e);
      alert("Failed to save layout: " + (e as Error).message);
    }
  }, [videoReady]);

  const handleLoadLayout = useCallback(async () => {
    if (!fabricRef.current) {
      alert("Canvas is not ready.");
      return;
    }
    
    const loadFromFile = (file: File) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const dataUrl = e.target?.result as string;
          
          if (!dataUrl || !dataUrl.startsWith('data:image/')) {
            throw new Error("Invalid image file");
          }
          
          console.log('📂 Loading layout background:', file.name);
          
          const canvas = fabricRef.current!;
          
          // Clear canvas before loading background
          canvas.clear();
          
          // Load image as non-interactive background layer
          fabric.Image.fromURL(dataUrl, (img) => {
            if (!img || !img.width || !img.height) {
              alert('Failed to load layout image.');
              return;
            }
            
            // Scale image to FIT (show full image, may have letterboxing)
            // Changed from Math.max to Math.min to ensure entire image is visible
            const canvasWidth = canvas.getWidth();
            const canvasHeight = canvas.getHeight();
            const imgWidth = img.width || 1;
            const imgHeight = img.height || 1;
            
            const scaleX = canvasWidth / imgWidth;
            const scaleY = canvasHeight / imgHeight;
            // Use Math.min to fit entire image within canvas (object-fit: contain)
            const scale = Math.min(scaleX, scaleY);
            
            // Calculate centering offsets to center the fitted image
            const scaledWidth = imgWidth * scale;
            const scaledHeight = imgHeight * scale;
            const left = (canvasWidth - scaledWidth) / 2;
            const top = (canvasHeight - scaledHeight) / 2;
            
            console.log('📐 Image dimensions:', { 
              original: { w: imgWidth, h: imgHeight },
              canvas: { w: canvasWidth, h: canvasHeight },
              scale: scale.toFixed(3),
              positioned: { left, top, width: scaledWidth, height: scaledHeight }
            });
            
            img.set({
              scaleX: scale,
              scaleY: scale,
              left: left,
              top: top,
              selectable: false,   // Cannot be selected
              evented: false,      // Cannot be interacted with
              hasControls: false,  // No controls
              hasBorders: false,   // No borders
              lockMovementX: true, // Locked position
              lockMovementY: true,
              lockRotation: true,
              lockScalingX: true,
              lockScalingY: true,
              originX: 'left',
              originY: 'top',
            });
            
            // Insert as bottom layer (z-index 0)
            canvas.add(img);
            img.sendToBack(); // Ensure it stays at the back
            
            // Set fallback background color (hidden by image)
            canvas.backgroundColor = '#000000';
            
            canvas.requestRenderAll();
            
            console.log('✅ Layout background loaded. You can now add objects on top!');
            alert(`✅ Layout loaded from ${file.name}!\n\nThe layout is now a static background.\nYou can add, move, and resize objects on top of it.`);
          }, { crossOrigin: 'anonymous' });
          
        } catch (err) {
          console.error("Failed to load layout:", err);
          alert("Failed to load layout: " + (err as Error).message);
        }
      };
      reader.onerror = () => {
        alert("Failed to read file.");
      };
      reader.readAsDataURL(file);
    };
    
    try {
      console.log('Opening file picker for layout image...');
      
      if ('showOpenFilePicker' in window) {
        try {
          const [handle] = await (window as any).showOpenFilePicker({
            types: [{
              description: 'Layout Images',
              accept: { 
                'image/png': ['.png'],
                'image/jpeg': ['.jpg', '.jpeg'],
                'image/jpg': ['.jpg']
              }
            }],
            multiple: false
          });
          
          const file = await handle.getFile();
          loadFromFile(file);
          return;
        } catch (e) {
          if ((e as Error).name === 'AbortError') {
            console.log('User cancelled load');
            return;
          }
          console.log('Falling back to input file method');
        }
      }
      
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/png,image/jpeg,image/jpg';
      input.style.display = 'none';
      
      input.onchange = (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          loadFromFile(file);
        }
        document.body.removeChild(input);
      };
      
      input.oncancel = () => {
        console.log('User cancelled load');
        document.body.removeChild(input);
      };
      
      document.body.appendChild(input);
      input.click();
      
    } catch (e) {
      console.error("Load layout failed:", e);
      alert("Failed to load layout: " + (e as Error).message);
    }
  }, []);

  const handleDeleteSelected = useCallback(() => {
    const canvas = fabricRef.current;
    if (!canvas) {
      alert("Canvas is not ready.");
      return;
    }
    const active = canvas.getActiveObject();
    if (!active) {
      alert("No object selected.");
      return;
    }
    canvas.remove(active);
    canvas.discardActiveObject();
    canvas.requestRenderAll();
  }, []);

  return (
    <div className={styles.screen}>
      <div className={styles.container}>
        {}
        <video 
          ref={videoRef} 
          playsInline 
          muted 
          className={styles.video} 
        />
        
        {}
        <canvas 
          ref={fabricCanvasRef} 
          className={styles.canvas}
        />
        
        {cameraError && (
          <div className={styles.cameraError}>
            <span>{cameraError}</span>
            <button className={styles.retryBtn} onClick={handleRetryCamera}>Retry</button>
            {devices.length > 0 && (
              <div className={styles.devicePicker}>
                <label htmlFor="deviceSel">Camera:</label>
                <select
                  id="deviceSel"
                  value={selectedDeviceId}
                  onChange={(e) => setSelectedDeviceId(e.target.value)}
                >
                  <option value="">Any</option>
                  {devices.map((d) => (
                    <option key={d.deviceId} value={d.deviceId}>
                      {d.label || `Camera ${d.deviceId.slice(0,6)}`}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}
        
        {/* Camera switch button for mobile */}
        {videoReady && !cameraError && (
          <button 
            className={styles.switchCameraBtn}
            onClick={handleSwitchCamera}
            title="Switch Camera"
          >
            🔄
          </button>
        )}
        
        {svgLibrary.length > 0 && (
          <div className={styles.svgLibrary}>
            {svgLibrary.map((asset) => {
              // Determine asset type from MIME for appropriate loader
              const isSvg = asset.dataUrl.includes('svg');
              const type = isSvg ? "svg" : "image";
              return (
                <button
                  key={asset.id}
                  type="button"
                  onClick={() => handleAddObject(type, asset.dataUrl)}
                >
                  {asset.name}
                </button>
              );
            })}
          </div>
        )}

  <ObjectToolbar 
    onAdd={handleAddObject} 
    onCapture={handleCapture} 
    onSaveLayout={handleSaveLayout} 
    onLoadLayout={handleLoadLayout}
    onDeleteSelected={handleDeleteSelected}
    onOpenGallery={onOpenGallery}
  />
        
        {isPreview && capturedImage && (
          <PreviewModal
            image={capturedImage}
            onClose={() => setIsPreview(false)}
            onFinalize={(img) => addToGallery(img)}
          />
        )}
      </div>
    </div>
  );
};

export default CameraOverlay;

